import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUpRight, Check, Instagram, Menu, ShoppingBag, X } from 'lucide-react';
import { BeatMark } from './BeatMark';
import { useCart } from '../contexts/CartContext';
const ICE_IMAGE = 'https://cdn.magicpatterns.com/uploads/knKE32EsZkYsqPa1ui7YrB/DSC03885.jpg';
const SOCIAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/m4VHGC1GgxgtGcoFZGpH81/IMG_0693.jpg';
const CAN_IMAGE = 'https://cdn.magicpatterns.com/uploads/wDu3fKhK1RBeGFe1tY72ry/DSC03888.jpg';
const VIDEO_MOMENT = 'https://cdn.magicpatterns.com/uploads/macVvEoEwdpGBSYDC8PDiv/CPSTHLM_30.mp4';
const PEOPLE_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/1KzvTsy3KfZ88WPDkaVLMn/WAGHORN_2026-03-22_18.jpg';
const PEOPLE_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/oLZUyGYJTdHH83PscaSwsS/IMG_0700.jpg';
const ICE_BOWL_IMAGE = 'https://cdn.magicpatterns.com/uploads/p4Sk3dNMD8P9BQwDabDdK8/DSC04025.jpg';
const CLOSING_IMAGE = 'https://cdn.magicpatterns.com/uploads/mUGwrPwqeYegR1sEkvQE7m/IMG_0669_4.png';
const CROWD_IMAGE = 'https://cdn.magicpatterns.com/uploads/q1QGnD1Kd2T9Us82VxWgCC/2025-11-09_-67.jpg';
const SILVER_HEART = 'https://cdn.magicpatterns.com/uploads/rJT9byhsXeU9KaTKr1LjUf/1.png';
const BEET_RED = 'https://cdn.magicpatterns.com/uploads/qULQc3HvRqojPLg7YXAYH5/Rodbeta.png';
const BEET_YELLOW = 'https://cdn.magicpatterns.com/uploads/3uGPvVV79oZz5PUQ2CQhbW/gulbeta.png';
const BEET_WHITE = 'https://cdn.magicpatterns.com/uploads/4xdunyqX2XsEhDvajcVBUY/VITBETA.png';
const CAN_LINEUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/4nRxNXZRgXs6U5T42kX5ab/Burk_1.png';
const INSTAGRAM_URL = 'https://www.instagram.com/beatyourbest_?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==';
const CONTACT_EMAIL = 'info@beatsoda.com';
const WEB3FORMS_KEY = 'a23b9419-6869-4092-bd1e-0a48a2461c5e';
const EASE = [0.23, 1, 0.32, 1] as const;
const rise = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: EASE
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const WORLD_WORDS = ['Music', 'Food', 'Friends', 'Travel', 'Sport', 'Nightlife'];
const NAV_LINKS: {
  label: string;
  id?: string;
  to?: string;
}[] = [{
  label: 'Shop',
  to: '/shop'
}, {
  label: 'The Drink',
  id: 'product'
}, {
  label: 'Flavours',
  id: 'flavours'
}, {
  label: 'World',
  id: 'world'
}, {
  label: 'People',
  id: 'people'
}, {
  label: 'Story',
  id: 'story'
}, {
  label: 'Join',
  id: 'join'
}];
const COMING_SOON = [{
  name: 'Yellow BEAT',
  symbol: BEET_YELLOW
}, {
  name: 'White BEAT',
  symbol: BEET_WHITE
}];
const INGREDIENTS = ['Beetroot', 'Grapefruit', 'Pomegranate', 'Lemon', 'Elderflower', 'Apple Juice Concentrate', 'Apple Cider Vinegar'];
export function LandingPage() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="w-full bg-beat-black font-sans text-white antialiased" data-id="element-14" data-mp="2gu3c2fxusebj">
      <SiteHeader onNavigate={scrollTo} data-id="element-15" data-mp="2lo7nbbe5j8if" />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-screen w-full overflow-hidden" data-id="element-16" data-mp="2pnq3xuyxwxfg">
        <motion.img src={ICE_IMAGE} alt="A cold BEAT can buried in crushed ice" initial={{
        opacity: 0,
        scale: 1.06
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.4,
        ease: EASE
      }} className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-17" data-mp="1szadzxrx4m1t" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.45) 45%, rgba(10,10,10,0.12) 75%, rgba(255,255,255,0.35) 100%)'
      }} data-id="element-18" data-mp="26vxajzfyla77" />

        <motion.div initial="hidden" animate="visible" variants={stagger} className="relative z-10 flex min-h-screen flex-col justify-end px-5 pb-20 pt-40 md:px-10 md:pb-24" data-id="element-19" data-mp="3if046b8wt4l9">
          <motion.p variants={rise} className="mb-6 text-[11px] font-medium tracking-[0.2em] text-white/70 uppercase sm:text-sm sm:tracking-widest" data-id="element-20" data-mp="3byrix8hfuvt1">
            REAL FRUIT · REAL ROOT · REAL FLAVOUR
          </motion.p>

          <motion.h1 variants={rise} className="max-w-[18ch] text-[13vw] font-medium leading-[1.05] tracking-tight sm:text-6xl md:text-7xl lg:text-8xl xl:text-[96px]" data-id="element-21" data-mp="3lxh0f05fae8r">
            A real way to experience  <span className="text-beat-red" data-id="element-22" data-mp="3utyf2dczwu99">life.</span>
          </motion.h1>

          <motion.div variants={rise} className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-center" data-id="element-23" data-mp="derj8p7uzzzk">
            <button onClick={() => scrollTo('product')} className="group inline-flex w-fit items-center gap-3 rounded-full bg-beat-red px-7 py-3.5 text-base font-medium text-white transition-colors duration-150 hover:bg-white hover:text-beat-black" style={{
            transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)'
          }} data-id="element-24" data-mp="8gzto1uospyj">
              Discover BEAT
              <ArrowUpRight size={18} className="transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-25" data-mp="162atvhgjms8b" />
            </button>
            <p className="max-w-xs text-base text-white/60" data-id="element-26" data-mp="s42u71s4hctg">
              A Real Root & Fruit Soda 
            </p>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.6,
        duration: 0.6
      }} className="absolute bottom-8 right-5 z-10 text-white/50 md:right-10" data-id="element-27" data-mp="ib5dn16oxxxh">
          <ArrowDown size={22} className="animate-bounce" data-id="element-28" data-mp="8bxsvsxmn10c" />
        </motion.div>
      </section>

      {/* ── 02: THIS IS BEAT (PRODUCT) ───────────────────────── */}
      <section id="product" className="scroll-mt-20 px-5 py-20 md:px-10 md:py-28" data-id="element-29" data-mp="3vk1gp47dtil8">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-30" data-mp="4coz4o2l734s">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-31" data-mp="261urz7xjjt1v">
            <motion.p variants={rise} className="mb-8 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-32" data-mp="2191osohq0srw">
              The Drink
            </motion.p>
            <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-33" data-mp="1sbhkf6okagd2">
              This is BEAT.
            </motion.h2>
            <motion.p variants={rise} className="mt-8 max-w-md text-xl leading-relaxed text-white/70" data-id="element-34" data-mp="2k1cknydyjqgi">
              A drink for the moments worth remembering. Bright, bold, rewarding, and balanced.
            </motion.p>

            <motion.div variants={rise} className="mt-10 flex flex-wrap items-center gap-5" data-id="element-35" data-mp="2b1h7xp4r7ned">
              <Link to="/shop" className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-beat-red px-7 text-base font-medium text-white transition-colors duration-150 hover:bg-white hover:text-beat-black" data-id="element-36" data-mp="14l57fhegltfm">
                Shop BEAT <ArrowUpRight size={18} data-id="element-37" data-mp="3ba5v2fwb7t31" />
              </Link>
              <button onClick={() => scrollTo('flavours')} className="inline-flex min-h-[52px] items-center gap-2 text-base font-medium text-white/60 transition-colors duration-150 hover:text-white" data-id="element-38" data-mp="3g7fpk9yk2uxq">
                Discover the flavours <ArrowDown size={18} data-id="element-39" data-mp="3p1aubgvrmjcu" />
              </button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 28
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          ease: EASE
        }} className="relative aspect-[4/5] w-full overflow-hidden rounded-3xl" data-id="element-40" data-mp="3tykjcvf8st05">
            <img src={CAN_IMAGE} alt="A BEAT can resting on a railing" className="h-full w-full object-cover" data-id="element-41" data-mp="29js9eozpaufk" />
          </motion.div>
        </div>
      </section>

      {/* ── 02B: THE FLAVOURS ────────────────────────────────── */}
      <section id="flavours" className="scroll-mt-20 rounded-t-[3rem] bg-[#F5F3EF] px-5 py-20 text-beat-black md:px-10 md:py-28" data-id="element-42" data-mp="1untwhinbjww2">
        <div className="mx-auto max-w-[1200px]" data-id="element-43" data-mp="1ppinfa4ulqew">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-end" data-id="element-44" data-mp="23hf9awfxb42u">
            <div data-id="element-45" data-mp="1bqj5elbuc9rp">
              <motion.p variants={rise} className="mb-6 text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-46" data-mp="1bs3u3ubh4yhn">
                The Flavours
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium leading-[1.05] tracking-tight md:text-6xl" data-id="element-47" data-mp="1ks8yrwpa9is4">
                Meet BEAT.
              </motion.h2>
              <motion.div variants={rise} className="mt-8 h-px w-16 bg-beat-red" data-id="element-48" data-mp="2w51oz32fdz62" />
            </div>
            <motion.p variants={rise} className="max-w-md text-xl leading-relaxed text-black/60" data-id="element-49" data-mp="2r7jcx3t433tu">
              Bold. Refreshing. Made from real ingredients. For real moments. We
              start with one — the red beet.
            </motion.p>
          </motion.div>

          {/* Red BEAT — available now */}
          <motion.article initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} className="mt-16" style={{
          isolation: 'auto'
        }} data-id="element-50" data-mp="2pqmcbl54okoh">
            <div className="bg-[#F5F3EF] py-4 md:py-8" style={{
            isolation: 'isolate'
          }} data-id="element-51" data-mp="3if1pe24p8sx6">
              <img src={CAN_LINEUP_IMAGE} alt="Red BEAT can shown from three sides, with beetroot artwork, ingredients and nutrition" className="mx-auto w-full max-w-4xl" style={{
              mixBlendMode: 'multiply'
            }} data-id="element-52" data-mp="3nfflqmwxuefd" />
            </div>
            <motion.div variants={rise} className="grid gap-10 border-t border-black/10 pt-10 md:grid-cols-2 md:pt-14" data-id="element-53" data-mp="57gj3ez1dfr1">
              <div data-id="element-54" data-mp="a0171zv9gymn">
                <div className="flex items-center gap-3" data-id="element-55" data-mp="a0jbhjs2uj5n">
                  <span className="h-3 w-3 rounded-full bg-beat-red" data-id="element-56" data-mp="2d4ky8a4doxew" />
                  <span className="text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-57" data-mp="2i2co6ylgq9gg">
                    Available now
                  </span>
                </div>
                <div className="mt-5 flex items-center gap-5" data-id="element-58" data-mp="24568xdzy6svz">
                  <img src={BEET_RED} alt="" aria-hidden="true" className="h-24 w-auto flex-shrink-0 mix-blend-darken md:h-32" data-id="element-59" data-mp="290lj9vyqy6nn" />
                  <h3 className="text-3xl font-medium tracking-tight md:text-5xl" data-id="element-60" data-mp="1uaxkb1e1fk33">
                    Red BEAT
                  </h3>
                </div>
                <p className="mt-5 max-w-md text-lg leading-relaxed text-black/60" data-id="element-61" data-mp="1ldderg9571mo">
                  Beetroot meets grapefruit, pomegranate and citrus in a vibrant
                  blend with floral notes and a crisp finish.
                </p>
              </div>
              <div className="flex flex-col justify-center gap-6 md:border-l md:border-black/10 md:pl-14" data-id="element-62" data-mp="1gczj6xoq9k5j">
                <div className="flex flex-wrap gap-x-6 gap-y-3 text-base text-black/70" data-id="element-63" data-mp="wqxn3jdsyljd">
                  {INGREDIENTS.map(item => <span key={item} data-id="element-64" data-mp="rt5yozcd9hjx">{item}</span>)}
                </div>
                <p className="border-t border-black/10 pt-6 text-base text-black/50" data-id="element-65" data-mp="16pn3t8typzy1">
                  250 ml · No added sugar · Low calorie · Caffeine free ·
                  Alcohol free
                </p>
              </div>
            </motion.div>
          </motion.article>

          {/* Yellow + White — coming soon */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} className="mt-6 grid gap-6 md:grid-cols-2 md:gap-8" data-id="element-66" data-mp="1as0aq0kg43we">
            {COMING_SOON.map(flavour => <motion.article key={flavour.name} variants={rise} className="flex items-center gap-6 rounded-3xl border border-black/10 p-8" data-id="element-67" data-mp="1khtmp2oi2l16">
                <img src={flavour.symbol} alt="" aria-hidden="true" className="h-24 w-auto flex-shrink-0 opacity-60 mix-blend-darken" data-id="element-68" data-mp="1ufhk60dhq6n1" />
                <div data-id="element-69" data-mp="21jqqqpal8b7m">
                  <h3 className="text-xl font-medium tracking-tight text-black/70 md:text-2xl" data-id="element-70" data-mp="21k8ue6zkxrpk">
                    {flavour.name}
                  </h3>
                  <p className="mt-1 text-base text-black/45" data-id="element-71" data-mp="2bgbwxdycfsd1">Coming soon</p>
                </div>
              </motion.article>)}
          </motion.div>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={rise} className="mt-12 text-base text-black/50" data-id="element-72" data-mp="2g9esfrmolbvv">
            No alcohol. No caffeine. Nothing artificial.
          </motion.p>
        </div>
      </section>

      {/* ── MARQUEE ──────────────────────────────────────────── */}
      <Marquee data-id="element-73" data-mp="2z6psheg509cd" />

      {/* ── 03: THE WORLD ────────────────────────────────────── */}
      <section id="world" className="scroll-mt-20 px-5 py-20 md:px-10 md:py-28" data-id="element-74" data-mp="ztbvhco9msue">
        <div className="mx-auto max-w-[1200px]" data-id="element-75" data-mp="zvexkv2p0cfh">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16" data-id="element-76" data-mp="14t6ktbse7adc">
            <div data-id="element-77" data-mp="3daew8w1z6u3t">
              <motion.p variants={rise} className="mb-6 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-78" data-mp="3d9vjdtr6mvwn">
                The World
              </motion.p>
              <motion.p variants={rise} className="text-3xl font-medium leading-snug text-white md:text-4xl" data-id="element-79" data-mp="34cl1vqnruaja">
                BEAT lives where good people, good taste and good experiences
                meet.
              </motion.p>
              <motion.p variants={rise} className="mt-6 max-w-sm text-lg leading-relaxed text-white/60" data-id="element-80" data-mp="3s0m6iohtx6tf">
                A dinner. A festival. A studio. A beach. A basement bar at 1am.
                The place changes. The feeling doesn't.
              </motion.p>
            </div>

            <motion.div variants={rise} data-id="element-81" data-mp="2gjcsihhgz6zi">
              <ul className="flex flex-wrap items-baseline gap-x-6 gap-y-2" data-id="element-82" data-mp="2bm33h2xzsxc7">
                {WORLD_WORDS.map(word => <li key={word} className="text-4xl font-medium tracking-tight text-white/85 md:text-5xl lg:text-6xl" data-id="element-83" data-mp="3def3a86tvrn">
                    {word}
                    <span className="text-beat-red" data-id="element-84" data-mp="86hew03k61ga">.</span>
                  </li>)}
              </ul>
              <p className="mt-10 text-2xl font-medium text-beat-red md:text-3xl" data-id="element-85" data-mp="3qg9jia14ow6a">
                Something is happening.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── MOMENT: THE FLOOR ────────────────────────────────── */}
      <Moment src={CROWD_IMAGE} alt="A packed room dancing in front of a DJ booth" label="Stockholm · Saturday" data-id="element-86" data-mp="r2fujhffu76f" />

      {/* ── 04: PEOPLE WITH A BEAT ───────────────────────────── */}
      <section id="people" className="scroll-mt-20 rounded-t-[3rem] bg-white px-5 py-20 text-beat-black md:px-10 md:py-28" data-id="element-87" data-mp="i19ef9gpaydp">
        <div className="mx-auto max-w-[1200px]" data-id="element-88" data-mp="2xvrsonjflvlp">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-89" data-mp="32t1mefdustfc">
            <div data-id="element-90" data-mp="2qaz2n2lcxjuc">
              <motion.p variants={rise} className="mb-6 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-91" data-mp="2q9e7pbv4ryvy">
                People with a beat
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-92" data-mp="2zalzrc7203hl">
                BEAT belongs to people with their own taste.
              </motion.h2>

              <motion.div variants={stagger} className="mt-10 grid gap-8 border-t border-black/10 pt-10" data-id="element-93" data-mp="27k7s3xpmvned">
                <PeopleItem title="Create" copy="Ideas that land. Nights that stay sharp all the way to the end." data-id="element-94" data-mp="2lbckqful3ff7" />
                <PeopleItem title="Connect" copy="Conversations you actually remember. Presence you can feel." data-id="element-95" data-mp="2gjkvc8n5zfqn" />
                <PeopleItem title="Move" copy="A body that shows up the next morning, ready to go again." data-id="element-96" data-mp="1st78cwjfs72x" />
              </motion.div>
            </div>

            <motion.div variants={rise} className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-97" data-mp="3nygg6kcn7r01">
              <div className="mt-12 md:mt-24" data-id="element-98" data-mp="3sx1e2d4f9ylb">
                <img src={PEOPLE_IMAGE_1} alt="Candid shot of a woman drinking BEAT at a party" className="aspect-[3/4] w-full object-cover rounded-3xl" data-id="element-99" data-mp="3svqbuhqmkywb" />
              </div>
              <div data-id="element-100" data-mp="6hq5na1i0tqo">
                <img src={PEOPLE_IMAGE_2} alt="Candid shot of a man drinking BEAT with sunglasses" className="aspect-[3/4] w-full object-cover rounded-3xl" data-id="element-101" data-mp="6g5d1pyqvkvg" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 05: THE WORLD IN MOTION ──────────────────────────── */}
      <Moment src={VIDEO_MOMENT} alt="Bartender grabbing a BEAT can from an ice bowl" label="Club · 23:45" isVideo data-id="element-102" data-mp="3evyss2l94sbg" />

      {/* ── 06: THE STORY ────────────────────────────────────── */}
      <section id="story" className="scroll-mt-20 px-5 py-20 md:px-10 md:py-28" data-id="element-103" data-mp="u6hq1f8uk7sw">
        <div className="mx-auto max-w-[1200px]" data-id="element-104" data-mp="y7l4bxb8xvs0">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-105" data-mp="2iqnmzbtgm3p9">
            <div data-id="element-106" data-mp="29ydbzsaqpu6y">
              <motion.p variants={rise} className="mb-6 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-107" data-mp="29xtrc3tpgrp8">
                The Story
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-108" data-mp="24xfttfpqd05g">
                São Paulo &rarr; Stockholm &rarr; BEAT.
              </motion.h2>
            </div>
            <div className="flex flex-col justify-center" data-id="element-109" data-mp="1v3fg4pgduml4">
              <motion.p variants={rise} className="text-xl leading-relaxed text-white/70 md:text-2xl" data-id="element-110" data-mp="1r0ah4wvih318">
                Someone tasted something amazing. They couldn't stop thinking about it. They brought the idea home. They met the right people. BEAT happened.
              </motion.p>
              <motion.p variants={rise} className="mt-6 text-xl leading-relaxed text-white/70 md:text-2xl" data-id="element-111" data-mp="3gfgx6ft3g1h7">
                The moment matters more than the beet.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 07: CLOSING ──────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-beat-red px-5 py-24 md:px-10 md:py-32" data-id="element-112" data-mp="2fj04iq81c9er">
        <img src={CLOSING_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-113" data-mp="2kgs8iilyflzb" />
        <div className="absolute inset-0" style={{
        backgroundColor: '#C8102E',
        mixBlendMode: 'multiply',
        opacity: 0.55
      }} data-id="element-114" data-mp="26koiz2m101t6" />
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={stagger} className="relative z-10 mx-auto flex max-w-[1200px] flex-col items-center text-center" data-id="element-115" data-mp="1wl79sb7kf5sg">
          <motion.h2 variants={rise} className="text-[13vw] font-medium leading-[1.05] tracking-tight sm:text-6xl md:text-7xl lg:text-[80px]" data-id="element-116" data-mp="3qzglinz8my3q">
            Don't waste
            <br data-id="element-117" data-mp="1k9wtlbknu8kr" />
            a heartbeat.
          </motion.h2>
        </motion.div>

        <div className="relative mx-auto mt-8 w-[68vw] max-w-[420px]" data-id="element-118" data-mp="1fb3hnnl0upmp">
          <div aria-hidden="true" className="absolute inset-[-12%] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0) 68%)'
        }} data-id="element-119" data-mp="1fcwob2vmx9up" />
          <img src={SILVER_HEART} alt="" aria-hidden="true" className="relative h-auto w-full" style={{
          mixBlendMode: 'multiply'
        }} data-id="element-120" data-mp="1p76h2lohoa5y" />
        </div>
      </section>

      {/* ── 08: JOIN ─────────────────────────────────────────── */}
      <section id="join" className="scroll-mt-20 px-5 py-20 md:px-10 md:py-28" data-id="element-121" data-mp="2cz3s4ak46y60">
        <div className="mx-auto max-w-[1200px]" data-id="element-122" data-mp="2800nusbykvz3">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-123" data-mp="280acocl9ox37">
            <div data-id="element-124" data-mp="26r1q9ro0cz6c">
              <motion.p variants={rise} className="mb-6 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-125" data-mp="26qjl25jdbama">
                Join
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-126" data-mp="21pvvloasf1u6">
                Join the BEAT.
              </motion.h2>
            </div>
            <motion.a variants={rise} href={`mailto:${CONTACT_EMAIL}`} className="text-2xl font-medium text-white underline decoration-beat-red decoration-2 underline-offset-8 transition-colors duration-150 hover:text-beat-red md:text-4xl" data-id="element-127" data-mp="2bia1mt8weum5">
              {CONTACT_EMAIL}
            </motion.a>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} className="grid gap-6 lg:grid-cols-3" data-id="element-128" data-mp="1xoabbhvxbl9y">
            <motion.a variants={rise} href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="group flex flex-col justify-between rounded-3xl bg-white/5 p-8 transition-colors duration-150 hover:bg-beat-red" data-id="element-129" data-mp="393p8oms1of5l">
              <Instagram size={26} className="mb-12 text-beat-red transition-colors duration-150 group-hover:text-white" data-id="element-130" data-mp="3gp1h1pjolhbi" />
              <div data-id="element-131" data-mp="32ps02nnnij6h">
                <h3 className="text-2xl font-medium" data-id="element-132" data-mp="32qbjy9wv3hn5">
                  Follow
                </h3>
                <p className="mt-2 flex items-center gap-2 text-base text-white/60 transition-colors duration-150 group-hover:text-white/90" data-id="element-133" data-mp="36rwvnrxntpvr">
                  @beatyourbest_
                  <ArrowUpRight size={18} data-id="element-134" data-mp="3aob30bjsbxc" />
                </p>
              </div>
            </motion.a>

            <NotifyForm data-id="element-135" data-mp="3loxltr07dcs4" />
            <StockForm data-id="element-136" data-mp="3lnco5silddq1" />
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer className="px-5 py-12 md:px-10" data-id="element-137" data-mp="h9xv6b2vjq6l">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-8 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between" data-id="element-138" data-mp="h94k4xwvuein">
          <BeatMark className="w-28" data-id="element-139" data-mp="vwmpzzm6lf6n" />
          <div className="flex flex-col gap-4 text-sm text-white/50 md:flex-row md:items-center md:gap-8" data-id="element-140" data-mp="vvle5sxcconv">
            <Link to="/shop" className="transition-colors duration-150 hover:text-white" data-id="element-141" data-mp="rwclkre1ewtw">
              Shop
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors duration-150 hover:text-white" data-id="element-142" data-mp="my1c4hrnmoc7">
              {CONTACT_EMAIL}
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="transition-colors duration-150 hover:text-white" data-id="element-143" data-mp="cy0mke36zbzk">
              Instagram
            </a>
            <p data-id="element-144" data-mp="4574k8wd1yi0">© {new Date().getFullYear()} BEAT Soda</p>
          </div>
        </div>
      </footer>
    </div>;
}
function SiteHeader({
  onNavigate
}: {
  onNavigate: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const {
    count,
    open: openCart
  } = useCart();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const go = (id: string) => {
    setOpen(false);
    window.setTimeout(() => onNavigate(id), 260);
  };
  const goTo = (path: string) => {
    setOpen(false);
    window.setTimeout(() => navigate(path), 260);
  };
  return <>
      <header className={`fixed inset-x-0 top-0 z-40 transition-colors duration-200 ${scrolled && !open ? 'bg-beat-black/85 backdrop-blur' : 'bg-transparent'}`} style={{
      transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)'
    }} data-id="element-145" data-mp="32h212pvyq52n">
        <div className="flex w-full max-w-full items-center justify-between gap-3 px-4 py-2 md:px-8 md:py-3" data-id="element-146" data-mp="29secolfx2zkv">
          <button onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })} aria-label="BEAT Soda — back to top" className="-ml-4 md:-ml-6" data-id="element-147" data-mp="2epmugkx5l1ll">
            <BeatMark className="w-36 md:w-48" data-id="element-148" data-mp="fdh7w1u9ueg3" />
          </button>

          <div className="flex items-center gap-2 sm:gap-3" data-id="element-149" data-mp="u12ck06solq4">
            <Link to="/shop" className="hidden rounded-full bg-beat-red px-6 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-white hover:text-beat-black sm:inline-flex" data-id="element-150" data-mp="u2n73pt404nz">
              Shop BEAT
            </Link>
            <button onClick={openCart} aria-label={`Open cart, ${count} item${count === 1 ? '' : 's'}`} className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-beat-black" data-id="element-151" data-mp="z0yg5ybkyb55">
              <ShoppingBag size={18} data-id="element-152" data-mp="3eqrpu8runzgp" />
              {count > 0 && <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-beat-red px-1 text-[11px] font-medium text-white" data-id="element-153" data-mp="3etnoy0ffou6q">
                  {count}
                </span>}
            </button>
            <button onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-beat-black" data-id="element-154" data-mp="1u8h5wdyj65dy">
              <Menu size={20} data-id="element-155" data-mp="16johd2k8mztd" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence data-id="element-156" data-mp="1bjaw5gfpso4a">
        {open && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="fixed inset-0 z-50 bg-beat-black" data-id="element-157" data-mp="1bklxl9lotjs8">
            <div className="flex h-full w-full flex-col px-5 py-4 md:px-10 md:py-5" data-id="element-158" data-mp="2r01a61cqesvz">
              <div className="flex items-center justify-between" data-id="element-159" data-mp="2vyw15qkurnr6">
                <BeatMark className="-ml-5 w-40 md:-ml-7 md:w-52" data-id="element-160" data-mp="2vycl6ferj9fs" />
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-beat-black" data-id="element-161" data-mp="2uhyym2db6uj6">
                  <X size={20} data-id="element-162" data-mp="2gi7h3rnl2w0f" />
                </button>
              </div>

              <nav className="flex flex-1 flex-col justify-center" data-id="element-163" data-mp="2gkai16q9y9jx">
                <motion.ul initial="hidden" animate="visible" variants={stagger} className="flex flex-col gap-2" data-id="element-164" data-mp="3d7owiycdbwfb">
                  {NAV_LINKS.map(link => <motion.li key={link.label} variants={rise} data-id="element-165" data-mp="33idpdh91l1u2">
                      <button onClick={() => link.to ? goTo(link.to) : go(link.id as string)} className={`text-4xl font-medium tracking-tight transition-colors duration-150 hover:text-beat-red md:text-6xl ${link.to ? 'text-white' : 'text-white/80'}`} data-id="element-166" data-mp="33gkgdvayi5iw">
                        {link.label}
                      </button>
                    </motion.li>)}
                </motion.ul>
              </nav>

              <div className="flex flex-col gap-3 border-t border-white/10 py-6 text-base text-white/50 md:flex-row md:items-center md:justify-between" data-id="element-167" data-mp="1dqwn0j3vrbrl">
                <a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors duration-150 hover:text-white" data-id="element-168" data-mp="1mpi92o8k98x4">
                  {CONTACT_EMAIL}
                </a>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors duration-150 hover:text-white" data-id="element-169" data-mp="1rmqj21jk26na">
                  @beatyourbest_ <ArrowUpRight size={16} data-id="element-170" data-mp="21l6ay6qepjxu" />
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function Marquee() {
  const line = 'Real fruit — Real root — Real flavour — ';
  return <div className="overflow-hidden bg-beat-red py-5" data-id="element-171" data-mp="2ff7cf88qtl1w">
      <motion.div animate={{
      x: ['0%', '-50%']
    }} transition={{
      duration: 48,
      ease: 'linear',
      repeat: Infinity
    }} className="flex w-max whitespace-nowrap" data-id="element-172" data-mp="2fgid2z6w6cos">
        {[0, 1].map(group => <span key={group} className="text-lg font-medium uppercase tracking-widest text-white md:text-xl" data-id="element-173" data-mp="1vcfz34mbpkxa">
            {line.repeat(6)}
          </span>)}
      </motion.div>
    </div>;
}
function Moment({
  src,
  alt,
  label,
  isVideo = false
}: {
  src: string;
  alt: string;
  label: string;
  isVideo?: boolean;
}) {
  return <section className="relative h-[80vh] w-full overflow-hidden md:h-screen" data-id="element-174" data-mp="1cjvialjwf69u">
      {isVideo ? <motion.video src={src} autoPlay loop muted playsInline initial={{
      opacity: 0,
      scale: 1.04
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-10%'
    }} transition={{
      duration: 1.1,
      ease: EASE
    }} className="h-full w-full object-cover" data-id="element-175" data-mp="17lak0ro7iwo1" /> : <motion.img src={src} alt={alt} initial={{
      opacity: 0,
      scale: 1.04
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-10%'
    }} transition={{
      duration: 1.1,
      ease: EASE
    }} className="h-full w-full object-cover" data-id="element-176" data-mp="svcpltzopaqe" />}
      <p className="absolute bottom-6 left-5 text-sm font-medium tracking-widest text-white/80 uppercase md:left-10" data-id="element-177" data-mp="jidymaolpv3o">
        {label}
      </p>
    </section>;
}
function PeopleItem({
  title,
  copy
}: {
  title: string;
  copy: string;
}) {
  return <motion.div variants={rise} data-id="element-178" data-mp="9p6l22jpj8si">
      <h3 className="text-3xl font-medium md:text-4xl" data-id="element-179" data-mp="xgkfqf5bzggn">
        {title}
        <span className="text-beat-red" data-id="element-180" data-mp="xf96w0x13iio">.</span>
      </h3>
      <p className="mt-4 max-w-xs text-lg leading-relaxed text-black/60" data-id="element-181" data-mp="12evkibgrr0s0">
        {copy}
      </p>
    </motion.div>;
}
async function sendToBeat(payload: Record<string, string>) {
  const response = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      access_key: WEB3FORMS_KEY,
      from_name: 'BEAT Website',
      to: CONTACT_EMAIL,
      ...payload
    })
  });
  return response.ok;
}
function NotifyForm() {
  const [form, setForm] = useState({
    email: '',
    phone: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.email) return;
    setStatus('loading');
    const ok = await sendToBeat({
      subject: 'BEAT — new community signup',
      email: form.email,
      phone: form.phone || 'Not given',
      message: `New community signup.\n\nEmail: ${form.email}\nPhone: ${form.phone || 'Not given'}`
    }).catch(() => false);
    setStatus(ok ? 'success' : 'error');
    if (ok) setForm({
      email: '',
      phone: ''
    });
  };
  return <motion.div variants={rise} className="flex flex-col justify-between rounded-3xl bg-white/5 p-8" data-id="element-182" data-mp="25u7e9z33ol64">
      <p className="mb-16 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-183" data-mp="18bed80r7582s">
        Community
      </p>
      <div data-id="element-184" data-mp="1da8uuzdfgqlb">
        <h3 className="text-2xl font-medium" data-id="element-185" data-mp="15ssswnnczg5b">
          Be first
        </h3>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-white/55" data-id="element-186" data-mp="15qq4u95rnx38">
          Next drops, new places to find BEAT, events and partnerships.
        </p>
        {status === 'success' ? <p className="mt-4 flex items-center gap-2 text-base font-medium text-beat-red" data-id="element-187" data-mp="vt1zkpajb76t">
            <Check size={18} data-id="element-188" data-mp="qvjxniuxps8b" /> You're in
          </p> : <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4" data-id="element-189" data-mp="mtyfpj3jmnr9">
            <input type="email" required value={form.email} onChange={event => setForm({
          ...form,
          email: event.target.value
        })} placeholder="Your email" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-190" data-mp="mw1fuvyetx9p" />
            <input type="tel" value={form.phone} onChange={event => setForm({
          ...form,
          phone: event.target.value
        })} placeholder="Phone (optional)" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-191" data-mp="38bqswhuyb46" />
            <button type="submit" disabled={status === 'loading'} className="self-start text-base font-medium text-white transition-colors duration-150 hover:text-beat-red disabled:opacity-50" data-id="element-192" data-mp="3bpt1fztjyg66">
              {status === 'loading' ? 'Sending…' : 'Join the list →'}
            </button>
            {status === 'error' && <p className="text-sm text-white/50" data-id="element-193" data-mp="c8bf1wzaqkmb">
                Didn't go through — try again.
              </p>}
          </form>}
      </div>
    </motion.div>;
}
const BUSINESS_TYPES = ['Event', 'Store', 'Bar', 'Restaurant', 'Café', 'Gym', 'Distributor', 'Other'];
function StockForm() {
  const [form, setForm] = useState({
    company: '',
    email: '',
    country: '',
    type: '',
    notes: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.company || !form.email || !form.country || !form.type) return;
    setStatus('loading');
    const ok = await sendToBeat({
      subject: 'BEAT — new business enquiry',
      email: form.email,
      company: form.company,
      country: form.country,
      business_type: form.type,
      notes: form.notes || 'No notes',
      message: `New business enquiry.\n\nCompany: ${form.company}\nType: ${form.type}\nCountry: ${form.country}\nEmail: ${form.email}\nNotes: ${form.notes || 'No notes'}`
    }).catch(() => false);
    setStatus(ok ? 'success' : 'error');
    if (ok) setForm({
      company: '',
      email: '',
      country: '',
      type: '',
      notes: ''
    });
  };
  return <motion.div variants={rise} className="flex flex-col justify-between rounded-3xl bg-white/5 p-8" data-id="element-194" data-mp="27otcchtefimq">
      <p className="mb-16 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-195" data-mp="22qrwe5r9rzfh">
        Business enquiries
      </p>
      <div data-id="element-196" data-mp="ccitjtmejrpm">
        <h3 className="text-2xl font-medium" data-id="element-197" data-mp="h993s0bizt34">
          Stock BEAT
        </h3>
        {status === 'success' ? <p className="mt-4 flex items-center gap-2 text-base font-medium text-beat-red" data-id="element-198" data-mp="m8lrena8146r">
            <Check size={18} data-id="element-199" data-mp="q9z0d68m1nhw" /> We'll be in touch
          </p> : <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4" data-id="element-200" data-mp="qbiht06064l9">
            <input type="text" required value={form.company} onChange={event => setForm({
          ...form,
          company: event.target.value
        })} placeholder="Venue or company" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-201" data-mp="v80j1q24jksu" />
            <input type="email" required value={form.email} onChange={event => setForm({
          ...form,
          email: event.target.value
        })} placeholder="Your email" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-202" data-mp="1e0b74ds4de6w" />
            <input type="text" required value={form.country} onChange={event => setForm({
          ...form,
          country: event.target.value
        })} placeholder="Country" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-203" data-mp="2ufgxwwn90ixo" />
            <select required value={form.type} onChange={event => setForm({
          ...form,
          type: event.target.value
        })} disabled={status === 'loading'} className={`w-full border-b border-white/25 bg-transparent pb-3 text-base focus:border-beat-red focus:outline-none disabled:opacity-50 ${form.type ? 'text-white' : 'text-white/40'}`} data-id="element-204" data-mp="3del6k4avoaot">
              <option value="" disabled className="bg-beat-black text-white/50" data-id="element-205" data-mp="4xqd1ffz02ne">
                Type of business
              </option>
              {BUSINESS_TYPES.map(option => <option key={option} value={option} className="bg-beat-black text-white" data-id="element-206" data-mp="stbh8erdapm8">
                  {option}
                </option>)}
            </select>
            <textarea rows={2} value={form.notes} onChange={event => setForm({
          ...form,
          notes: event.target.value
        })} placeholder="Volumes, timing, anything else" disabled={status === 'loading'} className="w-full resize-none border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-207" data-mp="j4juqm52sxip" />
            <button type="submit" disabled={status === 'loading'} className="self-start text-base font-medium text-white transition-colors duration-150 hover:text-beat-red disabled:opacity-50" data-id="element-208" data-mp="5fm3ehg55rk">
              {status === 'loading' ? 'Sending…' : 'Send enquiry →'}
            </button>
            {status === 'error' && <p className="text-sm text-white/50" data-id="element-209" data-mp="3f19dbaullwst">
                Didn't go through — try again.
              </p>}
          </form>}
      </div>
    </motion.div>;
}